// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ConsoleSession, UserRole } from '../types/auth';
import authService from '../services/authService';

interface AuthContextType {
  session: ConsoleSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  username: string;
  userRole: UserRole;
  isMfaRequired: boolean;
  login: (username: string, password: string, code?: string) => Promise<void>;
  logout: () => Promise<void>;
  mfaSetup: (code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<ConsoleSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize auth state from stored session
    const storedSession = authService.getSession();
    if (storedSession && authService.isAuthenticated()) {
      setSession(storedSession);
    }
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string, code?: string): Promise<void> => {
    try {
      const response = await authService.login(username, password, code);
      setSession(response.data);
    } catch (error) {
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      setSession(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear session locally even if server request fails
      setSession(null);
    }
  };

  const mfaSetup = async (code: string): Promise<void> => {
    try {
      await authService.mfaSetup(code);
      // Update session to reflect MFA setup completion
      const updatedSession = authService.getSession();
      setSession(updatedSession);
    } catch (error) {
      throw error;
    }
  };

  const contextValue: AuthContextType = {
    session,
    isAuthenticated: authService.isAuthenticated(),
    isLoading,
    username: authService.getUsername(),
    userRole: authService.getSessionRole(),
    isMfaRequired: authService.isMfaRequired(),
    login,
    logout,
    mfaSetup,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
